import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { FiBookOpen, FiSearch, FiDownload, FiPrinter, FiCalendar, FiExternalLink } from 'react-icons/fi';
import { colors } from '../../theme/colors';
import { formatDate as fmtDate } from '../../utils/formatters';
import { TRAN_TYPE_ROUTES, TRAN_TYPE_LABELS } from '../../utils/navigationHelper';
import { collection, getDocs, query, orderBy, where } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { useAuth } from '../../context/AuthContext';
import PageLayout from '../../components/PageLayout';
import SendWhatsAppButton from '../../components/SendWhatsAppButton';
import { getTransaction } from '../../utils/transactionHelper';
import { doc as fsDoc, getDoc } from 'firebase/firestore';
import useReportMemory from '../../hooks/useReportMemory';
import { addPrintBrand } from '../../utils/printBranding';

// Tran types that carry item lines we can expand in detailed mode
const ITEM_LINE_TRAN_TYPES = new Set(['SI', 'NSI', 'PI', 'NPI', 'SR', 'NSR', 'PR', 'NPR', 'PS', 'GRN', 'AS', 'RS', 'ST', 'DC', 'QT', 'SO', 'PO']);
// "Reference No" sources. For these, the reference lives on the PARENT transaction
// (cheque no for payments/receipts, vendor invoice no for purchases) so we fetch it.
const REF_PARENT_TYPES = new Set(['BP', 'BR', 'CP', 'CR', 'PI', 'NPI', 'IPI', 'PR', 'NPR']);
// Sales-side types whose Reference No is simply the document's own number.
const SALES_REF_TYPES = new Set(['SI', 'NSI', 'SR', 'NSR', 'PS']);

// TRAN_TYPE_ROUTES + TRAN_TYPE_LABELS come from the shared navigationHelper (single
// source of truth) so the ledger links AND labels EVERY tran type — incl. LON, ADV,
// PAY, FNF, DEP, FAD, MIP, PRC, PRO, REQ, CB — and can never drift out of sync again.

const formatCurrency = (amount) => {
  const dp = (typeof window !== 'undefined' && Number.isFinite(Number(window.__AMAL_DECIMAL_PLACES__)))
    ? Number(window.__AMAL_DECIMAL_PLACES__) : 2;
  if (!amount && amount !== 0) return '-';
  return new Intl.NumberFormat('en-PK', {
    minimumFractionDigits: dp,
    maximumFractionDigits: dp
  }).format(Math.abs(amount));
};

const formatBalance = (amount) => {
  const dp = (typeof window !== 'undefined' && Number.isFinite(Number(window.__AMAL_DECIMAL_PLACES__)))
    ? Number(window.__AMAL_DECIMAL_PLACES__) : 2;
  if (!amount && amount !== 0) return '-';
  const formatted = new Intl.NumberFormat('en-PK', {
    minimumFractionDigits: dp,
    maximumFractionDigits: dp
  }).format(Math.abs(amount));
  const suffix = amount >= 0 ? ' Dr' : ' Cr';
  return { formatted, suffix, isDebit: amount >= 0 };
};

// Dr/Cr tag for a NATURAL-TERMS running balance. The balance is stored positive on
// the account's normal side: assets/expenses are normal-Dr, while liabilities,
// equity and revenue are normal-Cr. So the SAME positive number reads "Dr" for an
// asset but "Cr" for a vendor/payable. The magnitude never changes — only the tag
// flips based on account type. (Fixes vendors showing a credit balance as "Dr".)
const balanceIsDebit = (amount, accountType) => {
  const normalDr = ['asset', 'expense'].includes((accountType || 'asset').toLowerCase());
  return amount >= 0 ? normalDr : !normalDr;
};
const balanceTag = (amount, accountType) => (balanceIsDebit(amount, accountType) ? 'Dr' : 'Cr');

const formatDate = (v) => fmtDate(v) || '-';

// ── Affected Accounts (contra accounts) ─────────────────────────────────────
// For each ledger row we show the OTHER accounts hit by the same document
// (e.g. a Sale Invoice row on a customer ledger → Sales Cr, GST Output Cr).
// Loaded ONLY when the user switches the column on, in chunks of 30 documents
// per query (Firestore 'in' limit), and cached by document so switching it
// off/on, changing the account, or going Back never re-reads the same document.
const AFF_CHUNK = 30;
const AFF_PARALLEL = 6;
const AFF_SCREEN_MAX = 4;   // lines shown on screen before "+N more"
const AFF_CACHE_CAP = 5000; // documents kept in memory before starting fresh

const fetchAffectedRows = async (companyId, txIds) => {
  const ref = collection(db, 'companies', companyId, 'subTransactions');
  const out = {};
  txIds.forEach(id => { out[id] = []; });
  const chunks = [];
  for (let i = 0; i < txIds.length; i += AFF_CHUNK) chunks.push(txIds.slice(i, i + AFF_CHUNK));
  let next = 0;
  const worker = async () => {
    while (next < chunks.length) {
      const chunk = chunks[next++];
      const snap = await getDocs(query(ref, where('transactionId', 'in', chunk)));
      snap.docs.forEach(d => {
        const r = d.data();
        if (!out[r.transactionId]) out[r.transactionId] = [];
        out[r.transactionId].push({
          id: d.id,
          accountId: r.accountId || null,
          accountName: r.accountName || '',
          accountCode: r.accountCode || '',
          debit: Number(r.debitAmount) || 0,
          credit: Number(r.creditAmount) || 0,
        });
      });
    }
  };
  await Promise.all(Array.from({ length: Math.min(AFF_PARALLEL, chunks.length) }, worker));
  return out;
};

// Keep the document's own line order (rows are saved as <docId>__s0, __s1 …).
// POS rows have random ids, so they fall back to debit lines first.
const affRowOrder = (r) => {
  const m = /__s(\d+)$/.exec(r.id || '');
  if (m) return Number(m[1]);
  return 100000 + (r.credit > 0 ? 1 : 0);
};

const escapeHtml = (s) => String(s ?? '')
  .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

const Ledger = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { activeCompany, activeFinancialYear, activeBranch } = useAuth();
  // Backward memory: restore this report's filters + generated rows on Back
  // (0 extra Firebase reads). savedMem is null on a first visit → defaults apply.
  const [savedMem, saveMemory] = useReportMemory('ledger');
  const [accounts, setAccounts] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState(() => savedMem?.selectedAccount ?? '');
  const [accountSearch, setAccountSearch] = useState(() => savedMem?.accountSearch ?? '');
  const [showAccountDropdown, setShowAccountDropdown] = useState(false);
  const dropdownRef = useRef(null);
  const [dateFrom, setDateFrom] = useState(() => savedMem?.dateFrom ?? '');
  const [dateTo, setDateTo] = useState(() => savedMem?.dateTo ?? '');
  const [tranTypeFilter, setTranTypeFilter] = useState(() => savedMem?.tranTypeFilter ?? 'all');
  const [ledgerEntries, setLedgerEntries] = useState(() => savedMem?.ledgerEntries ?? []);
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(() => savedMem?.hasSearched ?? false);
  const [financialYears, setFinancialYears] = useState([]);
  const [viewMode, setViewMode] = useState(() => savedMem?.viewMode ?? 'summarized');
  const [transactionCache, setTransactionCache] = useState({});
  const [linkedParty, setLinkedParty] = useState(null);

  // Merge-save into the Back-navigation memory (keeps fields saved earlier).
  const memRef = useRef(savedMem || null);
  const persist = useCallback((patch) => {
    memRef.current = { ...(memRef.current || {}), ...patch };
    saveMemory(memRef.current);
  }, [saveMemory]);

  // Affected Accounts column — hidden by default; the choice is remembered.
  const [showAffected, setShowAffected] = useState(() => savedMem?.showAffected ?? false);
  // docId -> { sig, rows }. `sig` is the ledger row's save-stamp when it was
  // loaded: if the document is edited later, the stamp changes and only that
  // document is re-read on the next Generate.
  const affMapRef = useRef(savedMem?.affectedMap || {});
  const affInFlight = useRef(new Set());
  const [affVersion, setAffVersion] = useState(0);
  const [loadingAffected, setLoadingAffected] = useState(false);
  const [expandedAff, setExpandedAff] = useState(() => new Set());

  const toggleAffected = () => {
    const v = !showAffected;
    if (v) {
      // Retry any documents that failed to load last time.
      const m = affMapRef.current;
      if (Object.values(m).some(x => x?.failed)) {
        const next = {};
        Object.entries(m).forEach(([k, x]) => { if (!x?.failed) next[k] = x; });
        affMapRef.current = next;
      }
    }
    setShowAffected(v);
    if (memRef.current?.hasSearched) persist({ showAffected: v });
  };

  // Load the linked customer/vendor when an account with linkedEntityId is selected
  useEffect(() => {
    let cancelled = false;
    setLinkedParty(null);
    const acc = accounts.find(a => a.id === selectedAccount);
    if (!acc || !acc.linkedEntityId || !acc.linkedEntityType) return undefined;
    if (acc.linkedEntityType !== 'customer' && acc.linkedEntityType !== 'vendor') return undefined;
    const collectionName = acc.linkedEntityType === 'customer' ? 'customers' : 'vendors';
    (async () => {
      try {
        const snap = await getDoc(fsDoc(db, 'companies', activeCompany.id, collectionName, acc.linkedEntityId));
        if (!cancelled && snap.exists()) setLinkedParty({ id: snap.id, ...snap.data(), _entityType: acc.linkedEntityType });
      } catch (_) {}
    })();
    return () => { cancelled = true; };
  }, [selectedAccount, accounts, activeCompany?.id]);
  const [loadingDetails, setLoadingDetails] = useState(false);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowAccountDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Navigate to transaction page
  const handleOpenTransaction = (tranTypeCode, transactionId) => {
    const route = TRAN_TYPE_ROUTES[tranTypeCode];
    if (route) {
      navigate(route, { state: { viewTransactionId: transactionId } });
    }
  };
  const [selectedFYId, setSelectedFYId] = useState(() => savedMem?.selectedFYId ?? '');

  // Summary values
  const [summary, setSummary] = useState(() => savedMem?.summary ?? {
    openingBalance: 0,
    totalDebit: 0,
    totalCredit: 0,
    closingBalance: 0
  });

  // Load financial years
  const loadFinancialYears = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      const fyRef = collection(db, 'companies', activeCompany.id, 'financialYears');
      const q = query(fyRef, orderBy('startDate', 'desc'));
      const snapshot = await getDocs(q);
      const fys = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
      setFinancialYears(fys);

      // Set default to active financial year
      if (activeFinancialYear?.id) {
        setSelectedFYId(activeFinancialYear.id);
      } else if (fys.length > 0) {
        setSelectedFYId(fys[0].id);
      }
    } catch (error) {
      console.error('Error loading financial years:', error);
    }
  }, [activeCompany, activeFinancialYear]);

  // Load accounts for dropdown
  const loadAccounts = useCallback(async () => {
    if (!activeCompany?.id) return;

    try {
      const accountsRef = collection(db, 'companies', activeCompany.id, 'accounts');
      const q = query(accountsRef, orderBy('code', 'asc'));
      const snapshot = await getDocs(q);

      const accountsList = snapshot.docs
        .map(doc => ({
          id: doc.id,
          ...doc.data()
        }))
        .filter(acc => acc.level === 5 || acc.isPostable === true); // Only show Level 5 postable accounts

      setAccounts(accountsList);
    } catch (error) {
      console.error('Error loading accounts:', error);
    }
  }, [activeCompany]);

  useEffect(() => {
    loadAccounts();
    loadFinancialYears();
    // Set default date range to current month — but ONLY on a fresh visit.
    // If we restored a saved view (Back navigation), keep the restored dates.
    if (!savedMem) {
      const today = new Date();
      const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
      setDateFrom(firstDay.toISOString().split('T')[0]);
      setDateTo(today.toISOString().split('T')[0]);
    }
  }, [loadAccounts, loadFinancialYears, savedMem]);

  // Handle incoming navigation state (e.g., from Trial Balance, P&L, Balance Sheet)
  const processedStateRef = useRef(false);
  useEffect(() => {
    if (location.state?.filterAccountId && accounts.length > 0 && !processedStateRef.current) {
      processedStateRef.current = true;
      const account = accounts.find(a => a.id === location.state.filterAccountId);
      if (account) {
        setSelectedAccount(account.id);
        setAccountSearch(`${account.code} - ${account.name}`);
        // Set date range to full financial year for comprehensive view
        if (activeFinancialYear?.startDate) {
          setDateFrom(activeFinancialYear.startDate);
        }
        if (activeFinancialYear?.endDate) {
          setDateTo(activeFinancialYear.endDate);
        }
      }
    }
  }, [location.state, accounts, activeFinancialYear]);

  // Load ledger entries from subTransactions collection
  const loadLedgerEntries = async () => {
    if (!activeCompany?.id || !selectedAccount) {
      alert('Please select an account');
      return;
    }

    if (!selectedFYId) {
      alert('Please select a financial year');
      return;
    }

    setLoading(true);
    setHasSearched(true);

    try {
      // Query subTransactions for this account with status = Posted (+ Draft OB for opening balances)
      const subTransRef = collection(db, 'companies', activeCompany.id, 'subTransactions');
      const conditions = [
        where('accountId', '==', selectedAccount),
        where('status', 'in', ['Posted', 'Draft']),
        where('financialYearId', '==', selectedFYId)
      ];

      const q = query(subTransRef, ...conditions);
      const snapshot = await getDocs(q);

      let allEntries = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })).filter(t => {
        // Include Posted entries always, and Draft entries only from OB (Opening Balance) transactions
        if (t.status === 'Draft' && t.tranTypeCode !== 'OB') return false;
        if (!activeBranch || activeBranch.id === 'all') return true;
        return t.branchId === activeBranch.id;
      });

      // Sort by docDate, then OB first, then by createdAt for same-day entries.
      //
      // WHY the OB tiebreaker: the opening-balance document is dated the FIRST
      // day of the financial year (transactionHelper.js -> docDate = fy.startDate),
      // so it collides with real trading done on that same day. createdAt is the
      // moment the row was KEYED IN, not the moment it happened — a user who
      // records a couple of invoices before getting round to entering opening
      // balances pushed OB to the bottom of the ledger, and the running Balance
      // column then read as if the business started with nothing. Opening stock
      // and cash logically precede every movement of that day, so OB is pinned
      // first. Sums (opening figure, total debit/credit, closing balance) are
      // order-independent and are unaffected.
      allEntries.sort((a, b) => {
        const dateCompare = (a.docDate || '').localeCompare(b.docDate || '');
        if (dateCompare !== 0) return dateCompare;
        const aOB = a.tranTypeCode === 'OB' ? 0 : 1;
        const bOB = b.tranTypeCode === 'OB' ? 0 : 1;
        if (aOB !== bOB) return aOB - bOB;
        return (a.createdAt || '').localeCompare(b.createdAt || '');
      });

      // Get account data for balance calculation
      const selectedAccountData = accounts.find(acc => acc.id === selectedAccount);
      const accountType = (selectedAccountData?.accountType || 'asset').toLowerCase();

      // Helper function to calculate balance change
      const calculateBalanceChange = (debit, credit) => {
        // Assets & Expenses: Debit increases, Credit decreases
        // Liabilities, Equity & Revenue: Credit increases, Debit decreases
        if (['asset', 'expense'].includes(accountType)) {
          return debit - credit;
        } else {
          return credit - debit;
        }
      };

      // Calculate OPENING BALANCE from all entries BEFORE dateFrom
      let openingBal = 0;
      if (dateFrom) {
        const entriesBeforeFromDate = allEntries.filter(e => (e.docDate || '') < dateFrom);
        entriesBeforeFromDate.forEach(entry => {
          const debit = entry.debitAmount || 0;
          const credit = entry.creditAmount || 0;
          openingBal += calculateBalanceChange(debit, credit);
        });
      }

      // Filter entries within the date range
      let periodEntries = allEntries;
      if (dateFrom) {
        periodEntries = periodEntries.filter(e => (e.docDate || '') >= dateFrom);
      }
      if (dateTo) {
        periodEntries = periodEntries.filter(e => (e.docDate || '') <= dateTo);
      }

      // Apply transaction type filter
      if (tranTypeFilter !== 'all') {
        periodEntries = periodEntries.filter(e => e.tranTypeCode === tranTypeFilter);
      }

      // Calculate running balance starting from opening balance
      let runningBalance = openingBal;
      const entriesWithBalance = periodEntries.map(entry => {
        const debit = entry.debitAmount || 0;
        const credit = entry.creditAmount || 0;
        runningBalance += calculateBalanceChange(debit, credit);

        return {
          ...entry,
          debit,
          credit,
          balance: runningBalance
        };
      });

      // Calculate summary
      const totalDebit = entriesWithBalance.reduce((sum, e) => sum + (e.debit || 0), 0);
      const totalCredit = entriesWithBalance.reduce((sum, e) => sum + (e.credit || 0), 0);
      const closingBal = entriesWithBalance.length > 0
        ? entriesWithBalance[entriesWithBalance.length - 1].balance
        : openingBal;

      const summaryObj = {
        openingBalance: openingBal,
        totalDebit,
        totalCredit,
        closingBalance: closingBal
      };
      setSummary(summaryObj);

      setLedgerEntries(entriesWithBalance);

      // Remember this generated view so Back restores it with 0 re-reads.
      persist({
        selectedAccount,
        accountSearch,
        dateFrom,
        dateTo,
        tranTypeFilter,
        selectedFYId,
        viewMode,
        showAffected,
        affectedMap: affMapRef.current,
        ledgerEntries: entriesWithBalance,
        summary: summaryObj,
        hasSearched: true
      });
    } catch (error) {
      console.error('Error loading ledger entries:', error);
    } finally {
      setLoading(false);
    }
  };

  // Get unique transaction types from entries
  const availableTranTypes = useMemo(() => {
    const types = new Set(ledgerEntries.map(e => e.tranTypeCode).filter(Boolean));
    return [...types].sort();
  }, [ledgerEntries]);

  // Lazy-fetch parent transactions:
  //  - always for REF_PARENT_TYPES (to show Reference No: cheque / vendor invoice)
  //  - additionally for item-line types in Detailed mode (to show qty/rate)
  useEffect(() => {
    if (!activeCompany?.id || ledgerEntries.length === 0) return;
    // JV/CB included so a settlement voucher can show which invoices it cleared.
    const needTypes = viewMode === 'detailed'
      ? new Set([...ITEM_LINE_TRAN_TYPES, ...REF_PARENT_TYPES, 'JV', 'CB'])
      : new Set([...REF_PARENT_TYPES, ...SALES_REF_TYPES, 'JV', 'CB']);
    const idsToFetch = [...new Set(
      ledgerEntries
        .filter(e => needTypes.has(e.tranTypeCode) && e.transactionId && !transactionCache[e.transactionId])
        .map(e => e.transactionId)
    )];
    if (idsToFetch.length === 0) return;
    let cancelled = false;
    setLoadingDetails(true);
    Promise.all(idsToFetch.map(id => getTransaction(activeCompany.id, id).catch(() => null)))
      .then(txs => {
        if (cancelled) return;
        const next = {};
        txs.forEach(t => { if (t) next[t.id] = t; });
        setTransactionCache(prev => ({ ...prev, ...next }));
      })
      .finally(() => { if (!cancelled) setLoadingDetails(false); });
    return () => { cancelled = true; };
  }, [viewMode, ledgerEntries, activeCompany, transactionCache]);

  // Reset cache if account/period/fy changes (we'll refetch as needed)
  useEffect(() => {
    setTransactionCache({});
  }, [selectedAccount, selectedFYId, dateFrom, dateTo]);

  // Load the other accounts of each document — ONLY while the column is on,
  // and only for documents not already loaded (or edited since).
  useEffect(() => {
    if (!showAffected || !activeCompany?.id || ledgerEntries.length === 0) return;
    const sigByTx = new Map();
    ledgerEntries.forEach(e => {
      if (!e.transactionId || sigByTx.has(e.transactionId)) return;
      if (affInFlight.current.has(e.transactionId)) return;
      const sig = e.createdAt || '';
      const cached = affMapRef.current[e.transactionId];
      if (cached && cached.sig === sig) return;
      sigByTx.set(e.transactionId, sig);
    });
    if (sigByTx.size === 0) return;
    const ids = [...sigByTx.keys()];
    ids.forEach(id => affInFlight.current.add(id));
    const companyId = activeCompany.id;
    setLoadingAffected(true);
    fetchAffectedRows(companyId, ids)
      .then(rowsByTx => {
        let base = affMapRef.current;
        if (Object.keys(base).length + ids.length > AFF_CACHE_CAP) base = {};
        const next = { ...base };
        ids.forEach(id => { next[id] = { sig: sigByTx.get(id), rows: rowsByTx[id] || [] }; });
        affMapRef.current = next;
        if (memRef.current?.hasSearched) persist({ affectedMap: next });
      })
      .catch(err => {
        console.error('Error loading affected accounts:', err);
        // Mark as failed (shows "—") so a bad connection can't cause a retry
        // loop of reads. Switching the column off and on again retries.
        const next = { ...affMapRef.current };
        ids.forEach(id => { next[id] = { sig: sigByTx.get(id), rows: [], failed: true }; });
        affMapRef.current = next;
      })
      .finally(() => {
        ids.forEach(id => affInFlight.current.delete(id));
        setLoadingAffected(affInFlight.current.size > 0);
        setAffVersion(v => v + 1);
      });
  }, [showAffected, ledgerEntries, activeCompany?.id, affVersion, persist]);

  const accountsById = useMemo(() => {
    const m = new Map();
    accounts.forEach(a => m.set(a.id, a));
    return m;
  }, [accounts]);

  // Other accounts of this row's document, netted per account, in document
  // order. null = not loaded yet; [] = nothing to show.
  const getAffected = (entry) => {
    if (!entry.transactionId) return [];
    const cached = affMapRef.current[entry.transactionId];
    if (!cached) return null;
    const byAcc = new Map();
    [...cached.rows]
      .sort((a, b) => affRowOrder(a) - affRowOrder(b))
      .forEach(r => {
        if (r.accountId && r.accountId === entry.accountId) return; // the ledger's own account
        const key = r.accountId || `n:${r.accountName || r.accountCode}`;
        const acc = r.accountId ? accountsById.get(r.accountId) : null;
        const cur = byAcc.get(key) || {
          key,
          name: acc?.name || r.accountName || r.accountCode || 'Account',
          code: acc?.code || r.accountCode || '',
          net: 0,
        };
        cur.net += r.debit - r.credit;
        byAcc.set(key, cur);
      });
    return [...byAcc.values()].filter(a => Math.abs(a.net) > 0.005);
  };
  const affText = (a) => `${a.net > 0 ? 'Dr' : 'Cr'} ${formatCurrency(a.net)}`;

  // Reference No per ledger row:
  //   • Payments / Receipts (BP/BR/CP/CR) → cheque no
  //   • Purchases (PI/NPI/IPI/PR/NPR)     → vendor invoice no
  //   • Sales (SI/NSI/SR/NSR/PS)          → the document's own number
  //   • anything else                     → typed Reference, else doc number
  // Cheque / vendor-invoice values live on the parent transaction (lazily cached).
  const getReferenceNo = (entry) => {
    const code = entry.tranTypeCode;
    const tx = transactionCache[entry.transactionId];
    // Sales docs: show the customer's own reference/PO if any — NOT a copy of the
    // Doc No (that's already its own column). The Sale Invoice stores this in
    // `customerPO`. Blank when there's no real reference.
    if (SALES_REF_TYPES.has(code)) return tx?.customerPO || tx?.reference || '-';
    if (code === 'BP' || code === 'BR' || code === 'CP' || code === 'CR') {
      return tx?.chequeNo || tx?.reference || '-';
    }
    if (code === 'PI' || code === 'NPI' || code === 'IPI' || code === 'PR' || code === 'NPR') {
      return tx?.vendorInvoiceNo || tx?.reference || '-';
    }
    return tx?.reference || '-';
  };

  // Particulars for a row: the stored description, plus — for receipts / payments
  // / settlement JVs that cleared invoices — the list of invoices settled, pulled
  // from the parent transaction we already cache (no extra reads).
  const buildDescription = (entry) => {
    const base = entry.description || '';
    const tx = transactionCache[entry.transactionId];
    const allocs = tx?.invoiceAllocations;
    if (Array.isArray(allocs) && allocs.length > 0) {
      const list = allocs.map(a => a.docNumber).filter(Boolean).join(', ');
      if (list) return base ? `${base} · vs ${list}` : `vs ${list}`;
    }
    return base || '-';
  };

  // Filter accounts based on search
  const filteredAccounts = useMemo(() => {
    if (!accountSearch.trim()) return accounts;
    const search = accountSearch.toLowerCase();
    return accounts.filter(acc =>
      (acc.code || '').toLowerCase().includes(search) ||
      (acc.name || '').toLowerCase().includes(search)
    );
  }, [accounts, accountSearch]);

  // Handle account selection
  const handleAccountSelect = (account) => {
    setSelectedAccount(account.id);
    setAccountSearch(`${account.code} - ${account.name}`);
    setShowAccountDropdown(false);
  };

  // Clear account selection
  const clearAccountSelection = () => {
    setSelectedAccount('');
    setAccountSearch('');
  };

  const handlePrint = () => {
    const selectedAccountData = accounts.find(acc => acc.id === selectedAccount);
    const selectedFY = financialYears.find(f => f.id === selectedFYId);
    const detailed = viewMode === 'detailed';
    const extraColsCount = detailed ? 2 : 0;

    // Show the balance figure with its Dr/Cr side (account-type aware) so the
    // printout matches the on-screen ledger — a vendor's positive balance reads
    // "Cr", an asset's reads "Dr". The magnitude never changes.
    const balWithTag = (amt) => `${formatCurrency(amt)} ${balanceTag(amt, selectedAccountData?.accountType)}`;

    const extraHeaders = detailed ? '<th class="amount">Qty</th><th class="amount">Rate</th>' : '';
    // Affected Accounts column prints only when the switch is on (all lines).
    const aff = showAffected;
    const affCellHtml = (entry) => {
      const list = getAffected(entry);
      if (!list || list.length === 0) return '<td class="aff">-</td>';
      return `<td class="aff">${list.map(a =>
        `<div class="aff-line"><span>${escapeHtml(a.name)}</span><span class="aff-amt ${a.net > 0 ? 'debit' : 'credit'}">${affText(a)}</span></div>`
      ).join('')}</td>`;
    };

    const rowsHtml = ledgerEntries.map(entry => {
      const mainRow = `
        <tr>
          <td>${formatDate(entry.docDate)}</td>
          <td>${entry.docNumber || '-'}</td>
          <td>${getReferenceNo(entry)}</td>
          <td><span class="tran-badge">${TRAN_TYPE_LABELS[entry.tranTypeCode] || entry.tranTypeCode || '-'}</span></td>
          ${aff ? affCellHtml(entry) : ''}
          <td>${entry.description || '-'}</td>
          ${detailed ? '<td class="amount">-</td><td class="amount">-</td>' : ''}
          <td class="amount debit">${entry.debit > 0 ? formatCurrency(entry.debit) : '-'}</td>
          <td class="amount credit">${entry.credit > 0 ? formatCurrency(entry.credit) : '-'}</td>
          <td class="amount">${balWithTag(entry.balance)}</td>
        </tr>`;
      if (!detailed) return mainRow;
      const itemLines = getItemLinesFor(entry);
      if (!itemLines) return mainRow;
      const detailRows = itemLines.map(line => `
        <tr class="detail-row">
          <td></td><td></td><td></td><td></td>${aff ? '<td></td>' : ''}
          <td style="padding-left:24px;color:#666;font-size:11px">↳ ${(line.itemName || line.description || line.itemCode || '-')}</td>
          <td class="amount" style="font-size:11px;color:#666">${line.quantity != null ? line.quantity : '-'}${line.unitOfMeasure ? ' ' + line.unitOfMeasure : ''}</td>
          <td class="amount" style="font-size:11px;color:#666">${line.unitPrice != null ? formatCurrency(line.unitPrice) : (line.unitCost != null ? formatCurrency(line.unitCost) : '-')}</td>
          <td class="amount" colspan="2" style="font-size:11px;color:#666">${line.lineTotal != null ? formatCurrency(line.lineTotal) : '-'}</td>
          <td></td>
        </tr>`).join('');
      return mainRow + detailRows;
    }).join('');

    const w = window.open('', '_blank');
    w.document.write(`<html><head><title>Account Ledger</title>
      <style>
        body{font-family:'Segoe UI',sans-serif;margin:24px;color:#1A1A1A;font-size:10.5px}
        h1{color:#800020;font-size:17px;margin-bottom:4px}
        .meta{color:#666;font-size:10px;margin-bottom:12px}
        .summary{display:flex;gap:24px;margin-bottom:12px;padding:10px;background:#f9f9f9;border-radius:8px}
        .summary-item{text-align:center}
        .summary-label{font-size:9.5px;color:#666;margin-bottom:3px}
        .summary-value{font-size:14px;font-weight:600}
        table{width:100%;border-collapse:collapse;margin-bottom:12px}
        /* Fixed layout (summary mode) pins each column width so Description gets
           the bulk of the page and the short columns stay tight & single-line.
           Detailed mode keeps auto layout because it has extra qty/price cols. */
        table.fixed{table-layout:fixed}
        th{background:#800020;color:white;padding:4px 6px;text-align:left;font-size:9.5px;text-transform:uppercase}
        td{padding:3px 5px;border-bottom:1px solid #ddd;line-height:1.3;vertical-align:top;font-size:7.5px;word-wrap:break-word;overflow-wrap:break-word}
        table.fixed td:nth-child(1),table.fixed th:nth-child(1){width:9%}
        table.fixed td:nth-child(2),table.fixed th:nth-child(2){width:10%}
        table.fixed td:nth-child(3),table.fixed th:nth-child(3){width:11%}
        table.fixed td:nth-child(4),table.fixed th:nth-child(4){width:7%}
        table.fixed td:nth-child(5),table.fixed th:nth-child(5){width:27%}
        table.fixed td:nth-child(6),table.fixed th:nth-child(6){width:12%}
        table.fixed td:nth-child(7),table.fixed th:nth-child(7){width:12%}
        table.fixed td:nth-child(8),table.fixed th:nth-child(8){width:12%}
        /* With the Affected Accounts column (9 columns) */
        table.fixed.aff td:nth-child(1),table.fixed.aff th:nth-child(1){width:8%}
        table.fixed.aff td:nth-child(2),table.fixed.aff th:nth-child(2){width:9%}
        table.fixed.aff td:nth-child(3),table.fixed.aff th:nth-child(3){width:9%}
        table.fixed.aff td:nth-child(4),table.fixed.aff th:nth-child(4){width:7%}
        table.fixed.aff td:nth-child(5),table.fixed.aff th:nth-child(5){width:21%}
        table.fixed.aff td:nth-child(6),table.fixed.aff th:nth-child(6){width:19%}
        table.fixed.aff td:nth-child(7),table.fixed.aff th:nth-child(7){width:9%}
        table.fixed.aff td:nth-child(8),table.fixed.aff th:nth-child(8){width:9%}
        table.fixed.aff td:nth-child(9),table.fixed.aff th:nth-child(9){width:9%}
        .aff-line{display:flex;justify-content:space-between;gap:6px}
        .aff-amt{font-family:'Consolas',monospace;white-space:nowrap}
        .amount{text-align:right;font-family:'Consolas',monospace;white-space:nowrap}
        .debit{color:#1565C0} .credit{color:#C62828}
        .opening-row,.closing-row{background:#f5f5f5;font-weight:600}
        .detail-row{background:#fafafa}
        .detail-row td{border-bottom:1px dashed #eee;padding:2px 6px}
        .tran-badge{font-size:9px;padding:1px 5px;border-radius:3px;background:#eee}
        tr{page-break-inside:avoid}
        @media print{body{margin:12px}}
      </style></head><body>
      <h1>Account Ledger${detailed ? ' — Detailed' : ''}</h1>
      <div class="meta">
        ${activeCompany?.companyName || 'Company'} |
        Account: ${selectedAccountData?.code} - ${selectedAccountData?.name} |
        Financial Year: ${selectedFY?.yearName || selectedFY?.yearCode || ''} |
        Period: ${formatDate(dateFrom)} to ${formatDate(dateTo)}
      </div>
      <div class="summary">
        <div class="summary-item">
          <div class="summary-label">Opening Balance</div>
          <div class="summary-value">${balWithTag(summary.openingBalance)}</div>
        </div>
        <div class="summary-item">
          <div class="summary-label">Total Debit</div>
          <div class="summary-value debit">${formatCurrency(summary.totalDebit)}</div>
        </div>
        <div class="summary-item">
          <div class="summary-label">Total Credit</div>
          <div class="summary-value credit">${formatCurrency(summary.totalCredit)}</div>
        </div>
        <div class="summary-item">
          <div class="summary-label">Closing Balance</div>
          <div class="summary-value">${balWithTag(summary.closingBalance)}</div>
        </div>
      </div>
      <table class="${detailed ? '' : 'fixed'}${aff ? ' aff' : ''}">
        <thead><tr>
          <th>Date</th>
          <th>Doc #</th>
          <th>Reference No</th>
          <th>Type</th>
          ${aff ? '<th>Affected Accounts</th>' : ''}
          <th>Description</th>
          ${extraHeaders}
          <th class="amount">Debit</th>
          <th class="amount">Credit</th>
          <th class="amount">Balance</th>
        </tr></thead>
        <tbody>
          <tr class="opening-row">
            <td>${formatDate(dateFrom)}</td>
            <td>-</td>
            <td>-</td>
            <td>-</td>
            ${aff ? '<td>-</td>' : ''}
            <td>Opening Balance</td>
            ${detailed ? '<td class="amount">-</td><td class="amount">-</td>' : ''}
            <td class="amount">-</td>
            <td class="amount">-</td>
            <td class="amount">${balWithTag(summary.openingBalance)}</td>
          </tr>
          ${rowsHtml}
          <tr class="closing-row">
            <td colspan="${5 + extraColsCount + (aff ? 1 : 0)}" style="text-align:right">Closing Balance</td>
            <td class="amount debit">${formatCurrency(summary.totalDebit)}</td>
            <td class="amount credit">${formatCurrency(summary.totalCredit)}</td>
            <td class="amount">${balWithTag(summary.closingBalance)}</td>
          </tr>
        </tbody>
      </table>
    </body></html>`);
    addPrintBrand(w);
    w.document.close();
    w.print();
  };

  const handleExport = () => {
    if (ledgerEntries.length === 0) return;

    const selectedAccountData = accounts.find(acc => acc.id === selectedAccount);
    const selectedFY = financialYears.find(f => f.id === selectedFYId);
    const detailed = viewMode === 'detailed';

    let csv = `Account Ledger${detailed ? ' (Detailed)' : ''} - ${selectedAccountData?.code} - ${selectedAccountData?.name}\n`;
    csv += `Financial Year: ${selectedFY?.yearName || selectedFY?.yearCode || ''}\n`;
    csv += `Period: ${formatDate(dateFrom)} to ${formatDate(dateTo)}\n\n`;
    // Affected Accounts column is exported only when the switch is on.
    const aff = showAffected;
    const affH = aff ? 'Affected Accounts,' : '';
    const affBlank = aff ? ',' : '';
    const affCsv = (entry) => {
      const list = getAffected(entry) || [];
      const txt = list.map(a => `${a.name} ${affText(a)}`).join('; ');
      return `"${(txt || '-').replace(/"/g, '""')}",`;
    };

    csv += detailed
      ? `Date,Doc Number,Reference No,Type,${affH}Description,Item,Qty,UOM,Rate,Line Total,Debit,Credit,Balance\n`
      : `Date,Doc Number,Reference No,Type,${affH}Description,Debit,Credit,Balance\n`;

    // Opening balance row
    csv += detailed
      ? `${formatDate(dateFrom)},-,-,-,${aff ? '-,' : ''}Opening Balance,,,,,,,${summary.openingBalance}\n`
      : `${formatDate(dateFrom)},-,-,-,${aff ? '-,' : ''}Opening Balance,-,-,${summary.openingBalance}\n`;

    ledgerEntries.forEach(entry => {
      const type = TRAN_TYPE_LABELS[entry.tranTypeCode] || entry.tranTypeCode || '';
      const desc = `"${(entry.description || '').replace(/"/g, '""')}"`;
      const refCsv = `"${String(getReferenceNo(entry) || '').replace(/"/g, '""')}"`;
      const affCol = aff ? affCsv(entry) : '';
      if (detailed) {
        csv += `${formatDate(entry.docDate)},${entry.docNumber || ''},${refCsv},${type},${affCol}${desc},,,,,,${entry.debit || ''},${entry.credit || ''},${entry.balance || ''}\n`;
        const itemLines = getItemLinesFor(entry);
        if (itemLines) {
          itemLines.forEach(line => {
            const itemName = `"${((line.itemName || line.description || line.itemCode || '') + '').replace(/"/g, '""')}"`;
            const rate = line.unitPrice != null ? line.unitPrice : (line.unitCost != null ? line.unitCost : '');
            csv += `,,,,${affBlank},${itemName},${line.quantity ?? ''},${line.unitOfMeasure || ''},${rate},${line.lineTotal ?? ''},,,\n`;
          });
        }
      } else {
        csv += `${formatDate(entry.docDate)},${entry.docNumber || ''},${refCsv},${type},${affCol}${desc},${entry.debit || ''},${entry.credit || ''},${entry.balance || ''}\n`;
      }
    });

    // Closing balance row
    csv += detailed
      ? `\n,,,,${affBlank}Total,,,,,,${summary.totalDebit},${summary.totalCredit},${summary.closingBalance}\n`
      : `\n,,,,${affBlank}Total,${summary.totalDebit},${summary.totalCredit},${summary.closingBalance}\n`;

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Ledger_${selectedAccountData?.code || 'unknown'}_${dateFrom}_${dateTo}${detailed ? '_detailed' : ''}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const selectedAccountData = accounts.find(acc => acc.id === selectedAccount);

  // Get item lines for a ledger entry (detailed mode only)
  const getItemLinesFor = (entry) => {
    if (viewMode !== 'detailed') return null;
    if (!ITEM_LINE_TRAN_TYPES.has(entry.tranTypeCode)) return null;
    const tx = transactionCache[entry.transactionId];
    if (!tx || !Array.isArray(tx.lines) || tx.lines.length === 0) return null;
    return tx.lines;
  };

  return (
    <PageLayout pageTitle="Ledger Report" breadcrumb="Reports / Accounting Reports / Ledger">
      <FiltersCard>
        <FiltersGrid>
          <FilterGroup>
            <Label>Financial Year *</Label>
            <Select value={selectedFYId} onChange={(e) => setSelectedFYId(e.target.value)}>
              <option value="">Select Financial Year</option>
              {financialYears.map(fy => (
                <option key={fy.id} value={fy.id}>
                  {fy.yearName || fy.yearCode}
                  {fy.isActive ? ' (Active)' : ''}
                </option>
              ))}
            </Select>
          </FilterGroup>

          <FilterGroup $span2>
            <Label>Account *</Label>
            <SearchableDropdown ref={dropdownRef}>
              <SearchInput
                type="text"
                placeholder="Search accounts by code or name..."
                value={accountSearch}
                onChange={(e) => {
                  setAccountSearch(e.target.value);
                  setShowAccountDropdown(true);
                  if (!e.target.value) setSelectedAccount('');
                }}
                onFocus={() => setShowAccountDropdown(true)}
              />
              {selectedAccount && (
                <ClearBtn onClick={clearAccountSelection}>×</ClearBtn>
              )}
              {showAccountDropdown && (
                <DropdownList>
                  {filteredAccounts.length === 0 ? (
                    <DropdownItem disabled>No accounts found</DropdownItem>
                  ) : (
                    filteredAccounts.slice(0, 50).map(account => (
                      <DropdownItem
                        key={account.id}
                        onClick={() => handleAccountSelect(account)}
                        $selected={selectedAccount === account.id}
                      >
                        <AccountCode>{account.code}</AccountCode>
                        <AccountName>{account.name}</AccountName>
                      </DropdownItem>
                    ))
                  )}
                  {filteredAccounts.length > 50 && (
                    <DropdownItem disabled>
                      ...and {filteredAccounts.length - 50} more (type to filter)
                    </DropdownItem>
                  )}
                </DropdownList>
              )}
            </SearchableDropdown>
          </FilterGroup>

          <FilterGroup>
            <Label>From Date</Label>
            <Input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
            />
          </FilterGroup>

          <FilterGroup>
            <Label>To Date</Label>
            <Input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
            />
          </FilterGroup>

          <FilterGroup>
            <Label>Transaction Type</Label>
            <Select
              value={tranTypeFilter}
              onChange={(e) => setTranTypeFilter(e.target.value)}
            >
              <option value="all">All Types</option>
              {Object.entries(TRAN_TYPE_LABELS).map(([code, label]) => (
                <option key={code} value={code}>{label}</option>
              ))}
            </Select>
          </FilterGroup>

          <FilterGroup>
            <Label>View Mode</Label>
            <Select value={viewMode} onChange={(e) => setViewMode(e.target.value)}>
              <option value="summarized">Summarized</option>
              <option value="detailed">Detailed (with items)</option>
            </Select>
          </FilterGroup>

          <FilterGroup>
            <Label>Affected Accounts</Label>
            <ToggleBox
              type="button"
              role="switch"
              aria-checked={showAffected}
              $on={showAffected}
              onClick={toggleAffected}
              title="Show the other accounts of each document"
            >
              <ToggleTrack $on={showAffected}><ToggleKnob $on={showAffected} /></ToggleTrack>
              {showAffected ? (loadingAffected ? 'Loading…' : 'Shown') : 'Hidden'}
            </ToggleBox>
          </FilterGroup>
        </FiltersGrid>

        <ButtonGroup>
          <Button onClick={handleExport} disabled={ledgerEntries.length === 0}>
            <FiDownload /> Export CSV
          </Button>
          <Button onClick={handlePrint} disabled={ledgerEntries.length === 0}>
            <FiPrinter /> Print
          </Button>
          {linkedParty && ledgerEntries.length > 0 && (
            <SendWhatsAppButton
              templateKey={linkedParty._entityType === 'vendor' ? 'vendorStatement' : 'customerStatement'}
              doc={{
                docDate: dateTo,
                totalAmount: summary.closingBalance,
                balanceAmount: summary.closingBalance,
                docNumber: '',
              }}
              party={linkedParty}
              extras={{
                title: linkedParty._entityType === 'vendor' ? 'Vendor Ledger' : 'Customer Ledger',
                partyLabel: linkedParty._entityType === 'vendor' ? 'Vendor' : 'Customer',
                rows: ledgerEntries.map(e => ({
                  date: e.docDate,
                  docNumber: e.docNumber,
                  description: e.description || e.tranTypeCode,
                  debit: e.debitAmount,
                  credit: e.creditAmount,
                })),
                openingBalance: summary.openingBalance,
                closingBalance: summary.closingBalance,
                periodLabel: `${dateFrom} to ${dateTo}`,
                docDate: dateTo,
                balanceAmount: summary.closingBalance,
              }}
              variant="button"
              label="Send Statement"
            />
          )}
          <Button primary onClick={loadLedgerEntries} disabled={loading}>
            <FiSearch /> {loading ? 'Loading...' : 'Generate Report'}
          </Button>
        </ButtonGroup>
      </FiltersCard>

      <ContentCard>
        {hasSearched && selectedAccountData && (
          <SummaryHeader>
            <AccountTitle>{selectedAccountData.code} - {selectedAccountData.name}</AccountTitle>
            <AccountSubtitle>
              {formatDate(dateFrom)} to {formatDate(dateTo)} | Type: {selectedAccountData.accountType}
            </AccountSubtitle>
            <SummaryGrid>
              <SummaryCard>
                <SummaryLabel>Opening Balance</SummaryLabel>
                <SummaryValue>{formatCurrency(summary.openingBalance)} <BalanceSuffix $isDebit={balanceIsDebit(summary.openingBalance, selectedAccountData?.accountType)}>{balanceTag(summary.openingBalance, selectedAccountData?.accountType)}</BalanceSuffix></SummaryValue>
              </SummaryCard>
              <SummaryCard>
                <SummaryLabel>Total Debit</SummaryLabel>
                <SummaryValue type="debit">{formatCurrency(summary.totalDebit)}</SummaryValue>
              </SummaryCard>
              <SummaryCard>
                <SummaryLabel>Total Credit</SummaryLabel>
                <SummaryValue type="credit">{formatCurrency(summary.totalCredit)}</SummaryValue>
              </SummaryCard>
              <SummaryCard>
                <SummaryLabel>Closing Balance</SummaryLabel>
                <SummaryValue>{formatCurrency(summary.closingBalance)} <BalanceSuffix $isDebit={balanceIsDebit(summary.closingBalance, selectedAccountData?.accountType)}>{balanceTag(summary.closingBalance, selectedAccountData?.accountType)}</BalanceSuffix></SummaryValue>
              </SummaryCard>
            </SummaryGrid>
          </SummaryHeader>
        )}

        <TableContainer>
          {loading ? (
            <EmptyState>
              <EmptyText>Loading ledger entries...</EmptyText>
            </EmptyState>
          ) : !hasSearched ? (
            <EmptyState>
              <EmptyIcon><FiBookOpen /></EmptyIcon>
              <EmptyText>Select an account and date range to view ledger</EmptyText>
            </EmptyState>
          ) : ledgerEntries.length === 0 ? (
            <EmptyState>
              <EmptyIcon><FiBookOpen /></EmptyIcon>
              <EmptyText>No posted transactions found for the selected criteria</EmptyText>
              <p style={{ fontSize: '14px', color: colors.text.tertiary }}>
                Opening Balance: {formatCurrency(summary.openingBalance)} {balanceTag(summary.openingBalance, selectedAccountData?.accountType)}
              </p>
            </EmptyState>
          ) : (
            <Table $alignTop={showAffected}>
              <Thead>
                <Tr>
                  <Th>Date</Th>
                  <Th>Doc Number</Th>
                  <Th>Reference No</Th>
                  <Th>Type</Th>
                  {showAffected && <AffTh>Affected Accounts</AffTh>}
                  <Th>Description</Th>
                  {viewMode === 'detailed' && <Th align="right">Qty</Th>}
                  {viewMode === 'detailed' && <Th align="right">Rate</Th>}
                  <Th align="right">Debit</Th>
                  <Th align="right">Credit</Th>
                  <Th align="right">Balance</Th>
                </Tr>
              </Thead>
              <Tbody>
                {viewMode === 'detailed' && loadingDetails && (
                  <Tr>
                    <Td colSpan={10 + (showAffected ? 1 : 0)} style={{ textAlign: 'center', fontStyle: 'italic', color: colors.text.secondary }}>
                      Loading item details...
                    </Td>
                  </Tr>
                )}
                {/* Opening Balance Row — hidden when the period opens at zero
                    (a blank 0.00 line is just noise; the real Opening-Balance
                    document, if any, shows as its own row below). */}
                {Math.abs(summary.openingBalance) > 0.005 && (
                <Tr isOpening>
                  <Td>{formatDate(dateFrom)}</Td>
                  <Td>-</Td>
                  <Td>-</Td>
                  <Td>-</Td>
                  {showAffected && <AffCell><AffMuted>—</AffMuted></AffCell>}
                  <Td>Opening Balance</Td>
                  {viewMode === 'detailed' && <AmountCell align="right">-</AmountCell>}
                  {viewMode === 'detailed' && <AmountCell align="right">-</AmountCell>}
                  <AmountCell align="right">-</AmountCell>
                  <AmountCell align="right">-</AmountCell>
                  <AmountCell align="right">
                    {formatCurrency(summary.openingBalance)} <BalanceSuffix $isDebit={balanceIsDebit(summary.openingBalance, selectedAccountData?.accountType)}>{balanceTag(summary.openingBalance, selectedAccountData?.accountType)}</BalanceSuffix>
                  </AmountCell>
                </Tr>
                )}

                {ledgerEntries.map((entry, index) => {
                  const itemLines = getItemLinesFor(entry);
                  return (
                  <React.Fragment key={`${entry.id}-${index}`}>
                  <Tr>
                    <Td>{formatDate(entry.docDate)}</Td>
                    <Td>
                      {entry.transactionId ? (
                        <DocNumber
                          onClick={() => handleOpenTransaction(entry.tranTypeCode, entry.transactionId)}
                          title="Click to open transaction"
                        >
                          {entry.docNumber || '-'} <FiExternalLink size={11} />
                        </DocNumber>
                      ) : (
                        <span>{entry.docNumber || '-'}</span>
                      )}
                    </Td>
                    <Td>{getReferenceNo(entry)}</Td>
                    <Td>
                      <TranBadge type={entry.tranTypeCode}>
                        {TRAN_TYPE_LABELS[entry.tranTypeCode] || entry.tranTypeCode || '-'}
                      </TranBadge>
                    </Td>
                    {showAffected && (() => {
                      const rowKey = `${entry.id}-${index}`;
                      const list = getAffected(entry);
                      if (list === null) return <AffCell><AffMuted>Loading…</AffMuted></AffCell>;
                      if (list.length === 0) return <AffCell><AffMuted>—</AffMuted></AffCell>;
                      const open = expandedAff.has(rowKey);
                      const shown = open ? list : list.slice(0, AFF_SCREEN_MAX);
                      const hidden = list.length - shown.length;
                      return (
                        <AffCell>
                          {shown.map(a => (
                            <AffLine key={a.key} title={a.code ? `${a.code} - ${a.name}` : a.name}>
                              <AffName>{a.name}</AffName>
                              <AffAmt $dr={a.net > 0}>{affText(a)}</AffAmt>
                            </AffLine>
                          ))}
                          {(hidden > 0 || (open && list.length > AFF_SCREEN_MAX)) && (
                            <AffMore
                              type="button"
                              onClick={() => setExpandedAff(prev => {
                                const s = new Set(prev);
                                if (s.has(rowKey)) s.delete(rowKey); else s.add(rowKey);
                                return s;
                              })}
                            >
                              {hidden > 0 ? `+${hidden} more (click to see all)` : 'Show less'}
                            </AffMore>
                          )}
                        </AffCell>
                      );
                    })()}
                    <Td>{buildDescription(entry)}</Td>
                    {viewMode === 'detailed' && <AmountCell align="right">-</AmountCell>}
                    {viewMode === 'detailed' && <AmountCell align="right">-</AmountCell>}
                    <AmountCell align="right" type={entry.debit > 0 ? 'debit' : undefined}>
                      {entry.debit > 0 ? formatCurrency(entry.debit) : '-'}
                    </AmountCell>
                    <AmountCell align="right" type={entry.credit > 0 ? 'credit' : undefined}>
                      {entry.credit > 0 ? formatCurrency(entry.credit) : '-'}
                    </AmountCell>
                    <AmountCell align="right">
                      {formatCurrency(entry.balance)} <BalanceSuffix $isDebit={balanceIsDebit(entry.balance, selectedAccountData?.accountType)}>{balanceTag(entry.balance, selectedAccountData?.accountType)}</BalanceSuffix>
                    </AmountCell>
                  </Tr>
                  {itemLines && itemLines.map((line, li) => (
                    <DetailRow key={`${entry.id}-${index}-line-${li}`}>
                      <Td></Td>
                      <Td></Td>
                      <Td></Td>
                      <Td></Td>
                      {showAffected && <Td></Td>}
                      <Td style={{ paddingLeft: 24, color: colors.text.secondary, fontSize: 12 }}>
                        ↳ {line.itemName || line.description || line.itemCode || '-'}
                      </Td>
                      <AmountCell align="right" style={{ fontSize: 12, color: colors.text.secondary }}>
                        {line.quantity ?? '-'}{line.unitOfMeasure ? ` ${line.unitOfMeasure}` : ''}
                      </AmountCell>
                      <AmountCell align="right" style={{ fontSize: 12, color: colors.text.secondary }}>
                        {line.unitPrice != null ? formatCurrency(line.unitPrice) : (line.unitCost != null ? formatCurrency(line.unitCost) : '-')}
                      </AmountCell>
                      <AmountCell align="right" colSpan={2} style={{ fontSize: 12, color: colors.text.secondary }}>
                        {line.lineTotal != null ? formatCurrency(line.lineTotal) : '-'}
                      </AmountCell>
                      <Td></Td>
                    </DetailRow>
                  ))}
                  </React.Fragment>
                  );
                })}

                {/* Closing Balance Row */}
                <Tr isClosing>
                  <Td colSpan={(viewMode === 'detailed' ? 7 : 5) + (showAffected ? 1 : 0)} style={{ textAlign: 'right', fontWeight: 600 }}>
                    Closing Balance
                  </Td>
                  <AmountCell align="right" type="debit" style={{ fontWeight: 600 }}>
                    {formatCurrency(summary.totalDebit)}
                  </AmountCell>
                  <AmountCell align="right" type="credit" style={{ fontWeight: 600 }}>
                    {formatCurrency(summary.totalCredit)}
                  </AmountCell>
                  <AmountCell align="right" style={{ fontWeight: 600 }}>
                    {formatCurrency(summary.closingBalance)} <BalanceSuffix $isDebit={balanceIsDebit(summary.closingBalance, selectedAccountData?.accountType)}>{balanceTag(summary.closingBalance, selectedAccountData?.accountType)}</BalanceSuffix>
                  </AmountCell>
                </Tr>
              </Tbody>
            </Table>
          )}
        </TableContainer>
      </ContentCard>
    </PageLayout>
  );
};

// Styled Components
const FiltersCard = styled.div`
  background: white;
  border-radius: ${colors.borderRadius.lg};
  box-shadow: ${colors.shadows.sm};
  padding: 20px;
  margin-bottom: 20px;
`;

const FiltersGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 16px;
  margin-bottom: 16px;
`;

const FilterGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  ${props => props.$span2 && 'grid-column: span 2;'}
`;

const Label = styled.label`
  font-size: 12px;
  font-weight: 600;
  color: ${colors.text.secondary};
  text-transform: uppercase;
`;

const Select = styled.select`
  padding: 10px 12px;
  border: 1px solid ${colors.neutral[300]};
  border-radius: ${colors.borderRadius.md};
  font-size: 14px;
  color: ${colors.text.primary};
  background: white;
  cursor: pointer;
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: ${colors.primary.main};
    box-shadow: 0 0 0 3px ${colors.primary.main}15;
  }
`;

const Input = styled.input`
  padding: 10px 12px;
  border: 1px solid ${colors.neutral[300]};
  border-radius: ${colors.borderRadius.md};
  font-size: 14px;
  color: ${colors.text.primary};
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: ${colors.primary.main};
    box-shadow: 0 0 0 3px ${colors.primary.main}15;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
`;

const Button = styled.button`
  padding: 10px 20px;
  border: none;
  border-radius: ${colors.borderRadius.md};
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.2s;

  ${props => props.primary ? `
    background: ${colors.primary.main};
    color: white;
    &:hover {
      background: ${colors.primary.dark};
    }
  ` : `
    background: ${colors.neutral[100]};
    color: ${colors.text.primary};
    &:hover {
      background: ${colors.neutral[200]};
    }
  `}

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const ContentCard = styled.div`
  background: white;
  border-radius: ${colors.borderRadius.lg};
  box-shadow: ${colors.shadows.sm};
  overflow: hidden;
`;

const SummaryHeader = styled.div`
  padding: 20px;
  border-bottom: 1px solid ${colors.neutral[200]};
  background: ${colors.neutral[50]};
`;

const AccountTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: ${colors.text.primary};
  margin: 0 0 8px 0;
`;

const AccountSubtitle = styled.p`
  font-size: 14px;
  color: ${colors.text.secondary};
  margin: 0;
`;

const SummaryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 20px;
  margin-top: 16px;
`;

const SummaryCard = styled.div`
  background: white;
  padding: 16px;
  border-radius: ${colors.borderRadius.md};
  border: 1px solid ${colors.neutral[200]};
`;

const SummaryLabel = styled.div`
  font-size: 12px;
  color: ${colors.text.secondary};
  margin-bottom: 4px;
`;

const SummaryValue = styled.div`
  font-size: 18px;
  font-weight: 600;
  color: ${props => props.type === 'debit' ? '#1565C0' : props.type === 'credit' ? '#C62828' : colors.text.primary};
`;

const TableContainer = styled.div`
  overflow-x: auto;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  ${props => props.$alignTop && 'td { vertical-align: top; }'}
`;

const Thead = styled.thead`
  background: ${colors.neutral[50]};
`;

const Th = styled.th`
  padding: 12px 16px;
  text-align: ${props => props.align || 'left'};
  font-size: 12px;
  font-weight: 600;
  color: ${colors.text.secondary};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border-bottom: 1px solid ${colors.neutral[200]};
`;

const Tbody = styled.tbody``;

const Tr = styled.tr`
  border-bottom: 1px solid ${colors.neutral[100]};
  transition: background 0.2s;

  &:hover {
    background: ${colors.neutral[50]};
  }

  ${props => props.isOpening && `
    background: ${colors.primary.main}08;
    font-weight: 500;
  `}

  ${props => props.isClosing && `
    background: ${colors.neutral[100]};
    font-weight: 600;
  `}
`;

const Td = styled.td`
  padding: 12px 16px;
  font-size: 14px;
  color: ${colors.text.primary};
  text-align: ${props => props.align || 'left'};
`;

const AmountCell = styled(Td)`
  font-family: 'SF Mono', 'Consolas', monospace;
  font-size: 13px;
  color: ${props => props.type === 'debit' ? '#1565C0' : props.type === 'credit' ? '#C62828' : colors.text.primary};
`;

const DetailRow = styled.tr`
  background: ${colors.neutral[50]};
  td {
    padding: 6px 16px;
    border-bottom: 1px dashed ${colors.neutral[200]};
  }
  &:hover {
    background: ${colors.neutral[100]};
  }
`;

const BalanceSuffix = styled.span`
  font-size: 11px;
  font-weight: 600;
  font-family: 'SF Mono', 'Consolas', monospace;
  color: ${props => props.$isDebit ? '#1565C0' : '#C62828'};
  margin-left: 4px;
`;

const DocNumber = styled.span`
  font-weight: 500;
  color: ${colors.primary.main};
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 6px;
  border-radius: 4px;
  transition: all 0.2s;

  &:hover {
    background: ${colors.primary.main}15;
    text-decoration: underline;
  }

  svg {
    opacity: 0.6;
  }

  &:hover svg {
    opacity: 1;
  }
`;

const TranBadge = styled.span`
  display: inline-block;
  padding: 4px 8px;
  border-radius: ${colors.borderRadius.sm};
  font-size: 11px;
  font-weight: 500;
  background: ${props => {
    const t = props.type;
    if (t === 'PI' || t === 'PR' || t === 'GRN' || t === 'PO') return '#E3F2FD';
    if (t === 'SI' || t === 'PS' || t === 'SR' || t === 'DC' || t === 'SO' || t === 'QT') return '#FFF3E0';
    if (t === 'CR' || t === 'BR') return '#E8F5E9';
    if (t === 'CP' || t === 'BP') return '#FFEBEE';
    if (t === 'JV' || t === 'OB') return '#F3E5F5';
    if (t === 'AS' || t === 'RS') return '#E0F7FA';
    return colors.neutral[200];
  }};
  color: ${props => {
    const t = props.type;
    if (t === 'PI' || t === 'PR' || t === 'GRN' || t === 'PO') return '#1565C0';
    if (t === 'SI' || t === 'PS' || t === 'SR' || t === 'DC' || t === 'SO' || t === 'QT') return '#E65100';
    if (t === 'CR' || t === 'BR') return '#2E7D32';
    if (t === 'CP' || t === 'BP') return '#C62828';
    if (t === 'JV' || t === 'OB') return '#7B1FA2';
    if (t === 'AS' || t === 'RS') return '#00838F';
    return colors.text.secondary;
  }};
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: ${colors.text.secondary};
`;

const EmptyIcon = styled.div`
  font-size: 48px;
  color: ${colors.neutral[300]};
  margin-bottom: 16px;
`;

const EmptyText = styled.p`
  font-size: 16px;
  margin-bottom: 8px;
`;

const SearchableDropdown = styled.div`
  position: relative;
  width: 100%;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 10px 35px 10px 12px;
  border: 1px solid ${colors.neutral[300]};
  border-radius: ${colors.borderRadius.md};
  font-size: 14px;
  color: ${colors.text.primary};
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: ${colors.primary.main};
    box-shadow: 0 0 0 3px ${colors.primary.main}15;
  }

  &::placeholder {
    color: ${colors.text.tertiary};
  }
`;

const ClearBtn = styled.button`
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  background: ${colors.neutral[200]};
  border: none;
  border-radius: 50%;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 14px;
  color: ${colors.text.secondary};
  transition: all 0.2s;

  &:hover {
    background: ${colors.neutral[300]};
    color: ${colors.text.primary};
  }
`;

const DropdownList = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  max-height: 300px;
  overflow-y: auto;
  background: white;
  border: 1px solid ${colors.neutral[300]};
  border-radius: ${colors.borderRadius.md};
  box-shadow: ${colors.shadows.lg};
  z-index: 100;
  margin-top: 4px;
`;

const DropdownItem = styled.div`
  padding: 10px 12px;
  display: flex;
  align-items: center;
  gap: 12px;
  cursor: ${props => props.disabled ? 'default' : 'pointer'};
  background: ${props => props.$selected ? colors.primary.main + '10' : 'transparent'};
  color: ${props => props.disabled ? colors.text.tertiary : colors.text.primary};
  font-size: 13px;
  transition: background 0.15s;

  &:hover {
    background: ${props => props.disabled ? 'transparent' : colors.neutral[100]};
  }

  &:not(:last-child) {
    border-bottom: 1px solid ${colors.neutral[100]};
  }
`;

const AccountCode = styled.span`
  font-family: 'SF Mono', 'Consolas', monospace;
  font-size: 12px;
  color: ${colors.primary.main};
  background: ${colors.primary.main}10;
  padding: 2px 6px;
  border-radius: 4px;
  white-space: nowrap;
`;

const AccountName = styled.span`
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

// ── Affected Accounts switch + column ──
const ToggleBox = styled.button`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 9px 12px;
  border: 1px solid ${props => props.$on ? colors.primary.main : colors.neutral[300]};
  border-radius: ${colors.borderRadius.md};
  background: ${props => props.$on ? `${colors.primary.main}0D` : 'white'};
  font-size: 14px;
  font-weight: 500;
  color: ${props => props.$on ? colors.primary.main : colors.text.secondary};
  cursor: pointer;
  transition: all 0.2s;
  text-align: left;

  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 3px ${colors.primary.main}25;
  }
`;

const ToggleTrack = styled.span`
  position: relative;
  flex-shrink: 0;
  width: 36px;
  height: 20px;
  border-radius: 10px;
  background: ${props => props.$on ? colors.primary.main : colors.neutral[300]};
  transition: background 0.2s;
`;

const ToggleKnob = styled.span`
  position: absolute;
  top: 2px;
  left: ${props => props.$on ? '18px' : '2px'};
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: white;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.25);
  transition: left 0.2s;
`;

const AffTh = styled(Th)`
  min-width: 230px;
  background: #FFF8E1;
`;

const AffCell = styled(Td)`
  min-width: 230px;
  background: #FFFDF5;
  vertical-align: top;
  font-size: 13px;
`;

const AffLine = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 12px;
  line-height: 1.5;
`;

const AffName = styled.span`
  color: ${colors.text.primary};
`;

const AffAmt = styled.span`
  font-family: 'SF Mono', 'Consolas', monospace;
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
  color: ${props => props.$dr ? '#1565C0' : '#C62828'};
`;

const AffMuted = styled.span`
  color: ${colors.text.tertiary};
`;

const AffMore = styled.button`
  margin-top: 2px;
  padding: 0;
  border: none;
  background: none;
  font-size: 12px;
  color: ${colors.primary.main};
  text-decoration: underline;
  cursor: pointer;
`;

export default Ledger;
